
import React, { Fragment, useState, useRef } from "react";
import useApiService from "../../hooks/useApiService";
import { RouteComponentProps } from "../../models/RouteComponent";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { formatDate } from "../../utils/DateUtils";

interface BillingSystemRecord {
  "id": string;
  "name": string;
  "subscription": true,
  "allow_admin_sign": false,
  "created_at": string;
  "deleted_at": string;
  "total_active_sites": number;
  "total_active_employees": number;
  "sites": BillingSystemSite[]
}

interface BillingSystemSite {
  "id": string;
  "name": string;
  "created_at": string;
  start_at: string;
  end_at: string;
  "recurrence_enabled": boolean;
}

export default function BillingSystemTable(props: RouteComponentProps) {
  const [expandedRows, setExpandedRows] = useState<any>({});
  const dt = useRef<any>(null);

  const { loading, data } = useApiService<BillingSystemRecord[]>({
    method: "GET",
    url: "billing-system",
    apiName: "core",
    params: {
      limit: 99999
    }
  });

  // Export to Excel function
  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    // Create flat data structure for export - only table columns
    const exportData: any[] = [];

    data.forEach((client) => {
      if (client.sites && client.sites.length > 0) {
        // Add each site as a separate row with client info
        client.sites.forEach((site) => {
          exportData.push({
            'Client': client.name,
            'Active Sites': client.total_active_sites,
            'Active Employees': client.total_active_employees,
            'Admin Sign': client.allow_admin_sign ? 'Yes' : 'No',
            'Subscription': client.subscription ? 'Active' : 'In Active',
            'Client Created Date': format(client.created_at),
            'Site Name': site.name,
            'Site Created Date': format(site.created_at),
            'Site Start Date': format(site.start_at),
            'Site End Date': format(site.end_at),
            'Recurring Daily Task Plan': site.recurrence_enabled ? 'Yes' : 'No'
          });
        });
      } else {
        // Add client row without site info
        exportData.push({
          'Client': client.name,
          'Active Sites': client.total_active_sites,
          'Active Employees': client.total_active_employees,
          'Admin Sign': client.allow_admin_sign ? 'Yes' : 'No',
          'Subscription': client.subscription ? 'Active' : 'In Active',
          'Client Created Date': format(client.created_at),
          'Site Name': 'No sites',
          'Site Created Date': '',
          'Site Start Date': '',
          'Site End Date': '',
          'Recurring Daily Task Plan': ''
        });
      }
    });

    // Create and download Excel file
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(exportData);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DSF_Billing_System_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };

  // Format date for display
  const format = (dateString: string) => {
    if (!dateString) return "N/A";
    return formatDate(dateString);
  };

  // Render subscription status
  const renderSubscriptionStatus = (rowData: BillingSystemRecord) => {
    return (
      <i
        style={{
          color: rowData.subscription ? "#2dd36f" : "#eb445a",
          width: "100%",
          textAlign: "center",
        }}
        className={classNames("pi", {
          "true-icon pi-check-circle": rowData.subscription || rowData.subscription === "Active",
          "false-icon pi-times-circle": !(
            rowData.subscription || rowData.subscription === "Active"
          ),
        })}
      ></i>
    );
  };

  // Render admin sign status
  const renderAdminSignStatus = (rowData: BillingSystemRecord) => {
    return (
      <i
        style={{
          color: rowData.allow_admin_sign ? "#2dd36f" : "#eb445a",
          width: "100%",
          textAlign: "center",
        }}
        className={classNames("pi", {
          "true-icon pi-check-circle": rowData.allow_admin_sign,
          "false-icon pi-times-circle": !(
            rowData.allow_admin_sign
          ),
        })}
      ></i>
    );
  };

  // Render recurrence status for sites
  const renderRecurrenceStatus = (site: BillingSystemSite) => {
    return (
      <i
        style={{
          color: site.recurrence_enabled ? "#2dd36f" : "#eb445a",
          width: "100%",
          textAlign: "center",
        }}
        className={classNames("pi", {
          "true-icon pi-check-circle": site.recurrence_enabled,
          "false-icon pi-times-circle": !(
            site.recurrence_enabled
          ),
        })}
      ></i>
    );
  };

  // Row expansion template for sites
  const rowExpansionTemplate = (data: BillingSystemRecord) => {
    return (
      <div>
        {data.sites && data.sites.length > 0 ? (
          <DataTable
            value={data.sites}
            className="p-datatable-sm"
            tableStyle={{ minWidth: "50rem" }}
          >
            <Column
              field="name"
              header="Site Name"
            />
            <Column
              field="created_at"
              header="Site Created Date"
              body={(site) => format(site.created_at)}
            />
            <Column
              field="start_at"
              header="Site Start Date"
              body={(site) => format(site.start_at)}
            />
            <Column
              field="end_at"
              header="Site End Date"
              body={(site) => format(site.end_at)}
            />
            <Column
              field="recurrence_enabled"
              header="Recurring Daily Task Plan"
              body={renderRecurrenceStatus}
            />
          </DataTable>
        ) : (
          <p>No sites available for this client.</p>
        )}
      </div>
    );
  };

  // Allow expansion check
  const allowExpansion = (rowData: BillingSystemRecord) => {
    return rowData.sites && rowData.sites.length > 0;
  };

  return (
    <Fragment>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Billing System Clients</h3>
          <Button
            type="button"
            icon="pi pi-file-excel"
            severity="success"
            onClick={exportToExcel}
            data-pr-tooltip="Export to Excel"
            disabled={!data || data.length === 0}
            label="Export Excel"
            className="p-button-outlined"
          />
        </div>
        <DataTable
          value={data || []}
          loading={loading}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="id"
          resizableColumns
          columnResizeMode="expand"
          className="p-datatable-striped"
          style={{
            flex: "1",
            display: "flex",
            flexDirection: "column",
          }}
          tableStyle={{
            height: "100%",
          }}
        >
          <Column
            expander={allowExpansion}
            style={{ textAlign: "center", width: 50 }}
            alignFrozen="right"
            frozen={true}
          />
          <Column
            field="name"
            header="Client"
            style={{ width: '20%' }}
            sortable
          />
          <Column
            field="total_active_sites"
            header="Active Sites"
            style={{ width: '10%' }}
            sortable
          />
          <Column
            field="total_active_employees"
            header="Active Employees"
            style={{ width: '10%' }}
            sortable
          />
          <Column
            field="allow_admin_sign"
            header="Admin Sign"
            body={renderAdminSignStatus}
            style={{ width: '15%' }}
          />
          <Column
            field="subscription"
            header="Subscription"
            body={renderSubscriptionStatus}
            style={{ width: '15%' }}
          />
          <Column
            field="created_at"
            header="Created Date"
            body={(rowData) => format(rowData.created_at)}
            style={{ width: '10%' }}
            sortable
          />
        </DataTable>
      </div>
    </Fragment>
  );
}
